/**
 * /activate page logic.
 *
 * Two sibling forms:
 *   - Email pre-check form (this module handles its submit). Branches to:
 *     * has_web_account / has_active_invite → terminal state message
 *     * ok_to_proceed → hand off to the details form (copy email, swap visibility)
 *   - Details form (handled entirely by the generic js-form framework in modules/forms.ts).
 */

import { resetForm, showError } from '../forms';
import { setLoading } from '../utils';

enum PreCheckStatus {
  HasWebAccount = 'has_web_account',
  HasActiveInvite = 'has_active_invite',
  OkToProceed = 'ok_to_proceed',
}

document.addEventListener('DOMContentLoaded', () => {
  const emailForm = document.querySelector<HTMLFormElement>('.js-request-invite-email-form');
  const detailsForm = document.querySelector<HTMLFormElement>('.js-request-invite-details-form');
  const intro = document.querySelector<HTMLElement>('.js-form-intro');
  const accountMsg = document.querySelector<HTMLElement>('.js-has-account-message');
  const inviteMsg = document.querySelector<HTMLElement>('.js-has-invite-message');
  if (!emailForm || !detailsForm || !intro || !accountMsg || !inviteMsg) {
    return;
  }

  const emailInput = emailForm.querySelector<HTMLInputElement>('input[name="email"]');
  const detailsEmail = detailsForm.querySelector<HTMLInputElement>('input[name="email"]');
  const route = emailForm.dataset.route;
  if (!emailInput || !detailsEmail || !route) {
    return;
  }

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

  // Wire the dismiss button on the error toast. The shared js-form framework only does this
  // for forms marked .js-form, but this pre-check form intentionally isn't one, so we mirror
  // the behavior manually for consistency with the rest of the site.
  const errorMsg = emailForm.querySelector<HTMLElement>('.js-form-error');
  errorMsg?.querySelector('.js-form-message-dismiss')?.addEventListener('click', (event) => {
    event.preventDefault();
    resetForm(emailForm);
    errorMsg.setAttribute('aria-hidden', 'true');
  });

  emailForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    // Auto-clear any prior error on retry so it doesn't keep covering the Continue button.
    errorMsg?.setAttribute('aria-hidden', 'true');

    if (!emailInput.checkValidity()) {
      emailInput.reportValidity();
      return;
    }

    setLoading(emailForm, true);

    try {
      const body = new FormData();
      body.append('email', emailInput.value);
      body.append('_csrf', csrfToken);

      const response = await fetch(route, {
        method: 'POST',
        credentials: 'same-origin',
        body,
        headers: { accept: 'application/json' },
      });

      if (!response.ok) {
        showError(emailForm);
        return;
      }

      const { status } = await response.json() as { status: PreCheckStatus };

      if (status === PreCheckStatus.HasWebAccount) {
        emailForm.hidden = true;
        intro.hidden = true;
        accountMsg.hidden = false;
        // Move focus so keyboard / screen-reader users land on the new state heading
        // (rather than being stranded on the now-hidden Continue button).
        accountMsg.querySelector<HTMLElement>('.request-invite-form__state-heading')?.focus();
      } else if (status === PreCheckStatus.HasActiveInvite) {
        emailForm.hidden = true;
        intro.hidden = true;
        inviteMsg.hidden = false;
        inviteMsg.querySelector<HTMLElement>('.request-invite-form__state-heading')?.focus();
      } else if (status === PreCheckStatus.OkToProceed) {
        detailsEmail.value = emailInput.value;
        emailForm.hidden = true;
        detailsForm.hidden = false;
        detailsForm.querySelector<HTMLInputElement>('input[name="firstName"]')?.focus();
      } else {
        showError(emailForm);
      }
    } catch (err) {
      console.error(err);
      showError(emailForm);
    } finally {
      setLoading(emailForm, false);
    }
  });
});
